import React, { useState, useCallback } from 'react';
import { formatCalendarDate, parseCalendarDate, tenantTodayDate } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import TimeOffDatesModal from '../components/TimeOffDatesModal';
import DatePicker from '../components/DatePicker';
import { useAuth } from '../contexts/AuthContext';
import { PageHeaderBand } from '../components/layout/PageHeaderBand';
import { displayValue } from '../features/fieldiq-handoff/contracts';
import { LoadFailureNotice } from '../components/ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../lib/load-failure';
import { getRoleLabel } from '../lib/roles';
import { invalidateTimeOffQueries } from '../lib/time-off-queries';
import {
  CalendarDays,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CalendarOff,
  Pencil,
  Plus,
  Users,
  UserRound,
  Sun,
} from 'lucide-react';
import { Tooltip } from '../components/ui/tooltip';

type Tab = 'pending' | 'approved' | 'rejected' | 'calendar';

/**
 * The request's status as a saturated pill, dark ink on a signal fill — the
 * Quotes / Rate Sheets badge. EXHAUSTIVE over the Postgres enum
 * `time_off_request_status` (pending, approved, rejected, cancelled). No tab
 * lists `cancelled`, but the enum can produce it, so it has an entry rather than
 * falling to the unknown pill. A value outside the enum reads `—` on slate, never
 * a guessed label.
 */
const REQUEST_STATUS: Record<string, { label: string; fill: string }> = {
  pending: { label: 'Pending', fill: 'bg-fi-signal-amber' },
  approved: { label: 'Approved', fill: 'bg-fi-signal-green' },
  rejected: { label: 'Rejected', fill: 'bg-fi-signal-red' },
  cancelled: { label: 'Cancelled', fill: 'bg-fi-signal-slate' },
};

const UNKNOWN_STATUS = { label: '—', fill: 'bg-fi-signal-slate' };

function RequestStatusBadge({ status }: { status: string | null | undefined }) {
  const skin = (status && REQUEST_STATUS[status]) || UNKNOWN_STATUS;
  return (
    <span
      className={`inline-flex w-fit items-center rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

/**
 * The 4px row rail, keyed on the same enum. A cancelled request is a dead row
 * and takes the quiet steel edge, as an inactive rate sheet does; a value
 * outside the enum keeps the width and claims no colour.
 */
const STATUS_RAIL: Record<string, string> = {
  pending: 'border-l-fi-amber',
  approved: 'border-l-fi-green',
  rejected: 'border-l-fi-red',
  cancelled: 'border-l-fi-steel',
};

/** Type is a category, not a state, so it takes the neutral outlined pill. */
const PILL =
  'inline-flex rounded-fi-pill border border-fieldiq-border px-2.5 py-1 text-[0.68rem] font-bold uppercase tracking-[0.06em] leading-none text-fieldiq-text';

const typeColors: Record<string, string> = {
  vacation: 'bg-blue-500',
  sick: 'bg-red-500',
  personal: 'bg-purple-500',
  statutory_holiday: 'bg-green-500',
  unpaid: 'bg-gray-500',
};

const typeLabels: Record<string, string> = {
  vacation: 'Vacation',
  sick: 'Sick',
  personal: 'Personal',
  statutory_holiday: 'Statutory Holiday',
  unpaid: 'Unpaid',
};

/**
 * The select's options. Derived from `typeLabels` rather than declared as a
 * second array, so the vocabulary cannot drift from the one this screen already
 * renders with — the closed-vocabulary-in-more-than-one-shape trap. The field
 * screen keeps its own label-keyed list and a typeMap to convert back; sending
 * the canonical enum value straight from here removes that round trip.
 */
const TIME_OFF_TYPE_VALUES = Object.keys(typeLabels);

/** Time-off bounds are calendar days. @see formatCalendarDate. */
const formatDate = (dateStr: string | null | undefined): string =>
  formatCalendarDate(dateStr, { month: 'short', day: 'numeric' }) ?? '';

/**
 * Calendar days the rows cover, each request counted inclusively (a one-day
 * request is 1). No endpoint returns this. Calendar days, weekends included —
 * the same span the Dates column shows, not working days, which would need the
 * crew's rota. Uses the shared local-midnight helper, so a DST change inside a
 * request does not cost or add a day. One unreadable date makes the whole
 * figure absent rather than a short total that reads as complete.
 */
function requestedDays(rows: any[]): number | undefined {
  let total = 0;
  for (const r of rows) {
    // Two calendar days, so the span is zone-free: no "today" is involved.
    const start = parseCalendarDate(r.start_date);
    const end = parseCalendarDate(r.end_date);
    const span = start && end
      ? Math.round(
          (Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()) -
            Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())) / 86_400_000,
        )
      : null;
    if (span == null || span < 0) return undefined;
    total += span + 1;
  }
  return total;
}

function pad(n: number) { return n < 10 ? `0${n}` : String(n); }

/** Names a day cell shows before the rest collapse behind "+N more". */
const DAY_NAME_CAP = 2;

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// --- Calendar Sub-component ---
/**
 * The month grid opens on, and marks, the TENANT's today (`companies.time_zone`)
 * — not the device's. With no zone there is no today to open on, so the grid is
 * not drawn rather than drawn around a guessed month.
 */
function CalendarView() {
  const timeZone = useTenantTimeZone();
  const today = tenantTodayDate(timeZone);
  if (!today) return <p className="py-12 text-center text-fieldiq-text">—</p>;
  return <CalendarMonth today={today} />;
}

function CalendarMonth({ today }: { today: Date }) {
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const from = `${year}-${pad(month + 1)}-01`;
  const to = `${year}-${pad(month + 1)}-${pad(daysInMonth)}`;

  // Same three states as the register below. An unreached calendar endpoint
  // used to paint an empty month under "No approved time off this month",
  // which is the sentence a dispatcher reads before scheduling somebody who
  // is away.
  const calendarQuery = useQuery({
    queryKey: ['time-off-calendar', from, to],
    queryFn: () => api.dispatch.timeOffCalendar(from, to).then(r => r.data.data ?? r.data ?? []),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const { data, isLoading } = calendarQuery;
  const calendarFailure = queryLoadFailure(calendarQuery);
  const entries: any[] = Array.isArray(data) ? data : [];

  // Group by employee
  const byEmployee = new Map<string, { name: string; items: any[] }>();
  entries.forEach((e: any) => {
    const key = e.user_id || e.employee_id || e.id;
    const name = e.employee_name || e.user_name || `${e.first_name || ''} ${e.last_name || ''}`.trim() || 'Unknown';
    if (!byEmployee.has(key)) byEmployee.set(key, { name, items: [] });
    byEmployee.get(key)!.items.push(e);
  });

  const prev = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const next = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const dayNumbers = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Check if a date falls within a range
  function isInRange(day: number, startDate: string, endDate: string) {
    const d = `${year}-${pad(month + 1)}-${pad(day)}`;
    return d >= startDate && d <= endDate;
  }

  // Build calendar grid
  const firstDayOfWeek = new Date(year, month, 1).getDay(); // 0=Sun
  const totalSlots = firstDayOfWeek + daysInMonth;
  const weeks = Math.ceil(totalSlots / 7);
  const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Build a lookup: day number → array of entries that cover that day
  const dayEntries = new Map<number, any[]>();
  entries.forEach((e: any) => {
    for (let d = 1; d <= daysInMonth; d++) {
      if (isInRange(d, e.start_date, e.end_date)) {
        if (!dayEntries.has(d)) dayEntries.set(d, []);
        dayEntries.get(d)!.push(e);
      }
    }
  });

  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;

  // Days expanded past DAY_NAME_CAP, keyed `YYYY-MM-DD` (built from year/month,
  // never toISOString). A Set, so expanding one day does not collapse another;
  // a full date rather than a day number, so the 14th's expansion does not
  // follow you into the next month.
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const toggleDayExpanded = (dayKey: string) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(dayKey)) next.delete(dayKey); else next.add(dayKey);
      return next;
    });
  };

  return (
    <div>
      {/* Month Nav */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={prev} aria-label="Previous month" className="p-2 rounded-lg bg-fieldiq-card text-fieldiq-text hover:text-white">
          <ChevronLeft size={18} />
        </button>
        <span className="font-semibold text-white">{MONTHS[month]} {year}</span>
        <button onClick={next} aria-label="Next month" className="p-2 rounded-lg bg-fieldiq-card text-fieldiq-text hover:text-white">
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-4">
        {Object.entries(typeColors).map(([type, color]) => (
          <div key={type} className="flex items-center gap-1.5 text-xs text-fieldiq-text">
            <span className={`w-3 h-3 rounded-sm ${color}`} />
            {typeLabels[type] || type}
          </div>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-fieldiq-text">Loading calendar...</div>
      ) : (
        <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border overflow-hidden">
          {/* Day-of-week headers */}
          <div data-testid="time-off-grid-header" className="grid grid-cols-7 border-b border-fieldiq-border">
            {DOW.map(d => (
              <div key={d} className="text-center text-[11px] font-semibold text-fieldiq-muted py-2">
                {d}
              </div>
            ))}
          </div>

          {/* ONE grid for the whole month — leading blanks, every day, trailing
              blanks — so the month has one column template and one set of
              widths. It used to be one grid per week, and each week then sized
              its own columns against its own names. */}
          <div data-testid="time-off-grid" className="grid grid-cols-7">
            {Array.from({ length: weeks * 7 }).map((_, slot) => {
              const dow = slot % 7;
              const day = slot - firstDayOfWeek + 1;
              const isValid = day >= 1 && day <= daysInMonth;
              if (!isValid) {
                return (
                  <div
                    key={`blank-${slot}`}
                    data-testid="time-off-day-blank"
                    className="min-h-[56px] border-b border-r border-fieldiq-border/40 p-1 bg-fieldiq-dark/30"
                  />
                );
              }
              const dayKey = `${year}-${pad(month + 1)}-${pad(day)}`;
              const isToday = isCurrentMonth && day === today.getDate();
              const items = dayEntries.get(day) || [];
              const isExpanded = expandedDays.has(dayKey);
              const hiddenCount = items.length - DAY_NAME_CAP;
              const shown = isExpanded ? items : items.slice(0, DAY_NAME_CAP);
              const dayLabel = `${MONTHS[month]} ${day}`;

              return (
                <div
                  key={dayKey}
                  data-testid={`time-off-day-${dayKey}`}
                  className="min-h-[56px] border-b border-r border-fieldiq-border/40 p-1"
                >
                  <div className={`text-[11px] font-medium mb-0.5 ${
                    isToday
                      ? 'text-fieldiq-accent font-bold'
                      : dow === 0 || dow === 6
                      ? 'text-fieldiq-muted'
                      : 'text-fieldiq-text'
                  }`}>
                    {isToday ? (
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-fieldiq-accent text-fieldiq-dark text-[10px] font-bold">
                        {day}
                      </span>
                    ) : (
                      day
                    )}
                  </div>
                  {shown.map((item: any, idx: number) => {
                    const itemType = item.time_off_type || item.type;
                    const color = typeColors[itemType] || 'bg-gray-500';
                    const empName = item.employee_name || item.user_name || `${item.first_name || ''} ${item.last_name || ''}`.trim() || '';
                    const shortName = empName.split(' ')[0] || typeLabels[itemType] || itemType;
                    return (
                      <div
                        key={idx}
                        className={`${color} opacity-80 rounded px-1 py-px text-[9px] text-white font-medium truncate mb-px`}
                        title={`${empName} — ${typeLabels[itemType] || itemType}: ${formatDate(item.start_date)} – ${formatDate(item.end_date)}`}
                      >
                        {shortName}
                      </div>
                    );
                  })}
                  {/* A button, not a <div>: the plain "+N more" was a dead end —
                      the people it hid could not be reached from the calendar.
                      Expands in place like the dispatch month grid (Aug 18),
                      because the point of a month is comparing days, and a
                      modal or a route replaces that with one day. */}
                  {hiddenCount > 0 && (
                    <button
                      type="button"
                      onClick={() => toggleDayExpanded(dayKey)}
                      aria-expanded={isExpanded}
                      aria-label={isExpanded
                        ? `Show fewer people off on ${dayLabel}`
                        : `Show ${hiddenCount} more ${hiddenCount === 1 ? 'person' : 'people'} off on ${dayLabel}`}
                      className="w-full text-left text-[9px] text-fieldiq-muted hover:text-fieldiq-text focus-visible:text-fieldiq-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-current rounded"
                    >
                      {isExpanded ? 'Show less' : `+${hiddenCount} more`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state text below calendar -- but only once we know it IS empty.

          ONE expression, not two sibling conditionals, and that is not a
          style choice: this component's outer element already carries enough
          children that adding a thirteenth tips TypeScript's JSX children
          inference into TS2746 ("expects a single child"), which is a
          complexity limit rather than a real error and reads as nonsense. A
          ternary keeps the child count where it was. */}
      {!isLoading && (
        calendarFailure ? (
          <LoadFailureNotice
            error={queryFailureError(calendarQuery)}
            kind={calendarFailure}
            subject="the time-off calendar"
            allowed="Admins, office staff and dispatchers"
          />
        ) : entries.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-fieldiq-muted">No approved time off this month</p>
          </div>
        ) : null
      )}
    </div>
  );
}

// --- Reject Modal ---
function RejectModal({ onConfirm, onCancel, isPending }: { onConfirm: (reason: string) => void; onCancel: () => void; isPending: boolean }) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-fieldiq-card rounded-xl p-5 w-full max-w-sm space-y-3" onClick={e => e.stopPropagation()}>
        <h3 className="font-semibold text-white">Reject Request</h3>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white resize-none"
          placeholder="Reason for rejection (optional)"
        />
        <div className="flex gap-2">
          <button onClick={onCancel} className="flex-1 py-2 text-sm rounded-lg bg-fieldiq-dark text-fieldiq-text">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={isPending}
            className="flex-1 py-2 text-sm rounded-lg bg-red-500 text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isPending && <Loader2 size={14} className="animate-spin" />}
            Reject
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Main Screen ---
export default function TimeOffScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('pending');
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [editing, setEditing] = useState<any | null>(null);

  // Request form. Office staff could not file time off at all until now: the
  // form lived only in FieldTimeOffScreen, which App.tsx routes exclusively
  // inside `if (isFieldWorker)`, so every office role reached this screen and
  // found an approval queue. The endpoint was never the obstacle — POST
  // /employee/time-off carries no requireRole and never has.
  const [showForm, setShowForm] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [type, setType] = useState(TIME_OFF_TYPE_VALUES[0]);
  const [reason, setReason] = useState('');

  // fe#697: GET /dispatch/time-off is gated, and a logistics user is refused
  // it. With no `error` in hand this query left `data` undefined, `requests`
  // fell to `[]`, and the register read "No pending requests" -- which told a
  // dispatcher there was nothing waiting on them while approvals piled up
  // behind a 403 they were never shown. The refusal is carried to the
  // register's third branch below and worded as being about access.
  const requestsQuery = useQuery({
    queryKey: ['dispatch-time-off', tab],
    queryFn: () => {
      if (tab === 'calendar') return Promise.resolve([]);
      return api.dispatch.timeOff({ status: tab }).then(r => r.data.data ?? r.data ?? []);
    },
    enabled: tab !== 'calendar',
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const { data, isLoading } = requestsQuery;
  const requestsFailure = queryLoadFailure(requestsQuery);
  const requests: any[] = Array.isArray(data) ? data : [];

  /**
   * Files the request through the SAME endpoint the field app uses. Not a
   * parallel office-only route: `/employee/*` is a per-user surface rather than
   * a field-worker one, it derives user_id from the JWT, and the record it
   * writes needs nothing an office employee lacks — time_off_requests has no
   * job, no crew assignment and in fact no foreign keys at all.
   *
   * Every office role reaches this, deliberately, matching the API. Gating the
   * form on a role list would have re-created the bug it fixes: TNT has no
   * `office_staff` row, so the person who asked for this is an `admin`, and
   * estate-wide `office_staff` exists on exactly one tenant.
   */
  /**
   * Every view of `time_off_requests` this app renders, refreshed together.
   * The key list and its history live in `lib/time-off-queries.ts`; the field
   * screen's mutations call the same helper, so neither side can name a subset.
   */
  const invalidateTimeOff = useCallback(() => {
    invalidateTimeOffQueries(queryClient);
  }, [queryClient]);

  const submitMutation = useMutation({
    mutationFn: (payload: any) => api.employee.submitTimeOff(payload),
    onSuccess: () => {
      invalidateTimeOff();
      setShowForm(false);
      setStartDate('');
      setEndDate('');
      setType(TIME_OFF_TYPE_VALUES[0]);
      setReason('');
    },
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.dispatch.approveTimeOff(id),
    onSuccess: invalidateTimeOff,
    // This screen had NO onError at all, which was survivable only while every
    // refusal was a race nobody could trigger. The server now returns a 409
    // `self_approval`, and without a handler that refusal renders as nothing
    // happening — the button clicks, the row stays, no message. Same gap the
    // timesheet approval queue carries.
    onError: () => {
      // A refusal usually means this row moved underneath us (someone else
      // approved it, or the dates were edited). Re-read so every view agrees
      // with the server before the operator clicks again.
      invalidateTimeOff();
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => api.dispatch.rejectTimeOff(id, reason),
    onSuccess: () => {
      invalidateTimeOff();
      setRejectId(null);
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ id, startDate, endDate }: { id: string; startDate: string; endDate: string }) =>
      api.dispatch.editTimeOff(id, { startDate, endDate }),
    onSuccess: () => {
      invalidateTimeOff();
      setEditing(null);
    },
  });

  // Only a request that is still live can be re-dated; rejected and cancelled are terminal.
  const canEdit = (status: string) => status === 'pending' || status === 'approved';

  /**
   * Whether a row was filed by whoever is looking at it. Drives the disabled
   * approve button below — the explanation, not the enforcement. The refusal
   * itself is POST /dispatch/time-off/:id/approve returning 409, because three
   * tenants have exactly one approver-capable user, which makes "a request only
   * the requester can approve" a reachable state rather than a hypothesis. A
   * hidden button would leave that state silent instead of explained.
   */
  const isOwnRequest = (req: any) => Boolean(user?.id) && req.user_id === user?.id;

  const handleSubmit = () => {
    if (!startDate || !endDate) return;
    // `type` already holds the canonical enum value, so nothing is mapped on
    // the way out. `reason` is optional and must go as undefined rather than
    // '' — the handler stores `reason || null`, and an empty string would read
    // back as a reason that was given and was blank.
    submitMutation.mutate({
      startDate,
      endDate,
      timeOffType: type,
      reason: reason.trim() || undefined,
    });
  };

  /**
   * One row's derived display, shared by the table and the mobile cards so the
   * two cannot disagree. Every expression here is the one the card list used,
   * moved unchanged.
   */
  const describeRow = (req: any) => {
    const status: string = req.status || tab;
    return {
      name: req.employee_name || req.user_name || `${req.first_name || ''} ${req.last_name || ''}`.trim() || 'Employee',
      status,
      own: isOwnRequest(req),
      dates: `${formatDate(req.start_date)} – ${formatDate(req.end_date)}`,
      typeLabel: typeLabels[req.time_off_type || req.type] || req.time_off_type || req.type,
      // The shared mutation carries the id it was called with, so the refusal
      // renders under the row it belongs to without a second piece of state
      // keyed by row.
      approveError:
        approveMutation.isError && approveMutation.variables === req.id
          ? (approveMutation.error as any)?.response?.data?.error
            || 'Failed to approve request'
          : null,
    };
  };
  type RowView = ReturnType<typeof describeRow>;

  const renderNotes = (req: any, row: RowView) => {
    const ownPending = row.own && tab === 'pending';
    const rejection = req.status === 'rejected' && req.rejection_reason;
    if (!req.reason && !rejection && !ownPending && !row.approveError) {
      return <span className="text-sm text-fieldiq-muted">—</span>;
    }
    return (
      <div className="space-y-1">
        {req.reason && <p className="text-xs text-fieldiq-muted">{req.reason}</p>}
        {rejection && <p className="text-xs text-fi-red">Reason: {req.rejection_reason}</p>}
        {ownPending && (
          <p className="text-xs text-fieldiq-muted">
            You filed this, so it needs a second person to approve it.
          </p>
        )}
        {row.approveError && <p className="text-xs text-fi-red">{row.approveError}</p>}
      </div>
    );
  };

  /**
   * The list and every control on it share ONE gate:
   * requireRole('admin', 'office_staff', 'supervisor', 'dispatcher') on GET
   * /dispatch/time-off and on approve, reject and PATCH alike
   * (dispatch.routes.ts). A caller who can see a row can use its controls, so
   * no per-control role check is added here — it would be a second copy of a
   * gate the list already applies, and could never fire.
   */
  const renderActions = (req: any, row: RowView) => {
    if (!canEdit(row.status) && tab !== 'pending') return <span className="text-sm text-fieldiq-muted">—</span>;
    return (
      <div className="flex items-center gap-2">
        {canEdit(row.status) && (
          <Tooltip content="Edit dates">
            <button
              onClick={() => setEditing(req)}
              className="flex h-9 items-center gap-1.5 whitespace-nowrap rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 text-xs font-bold text-fieldiq-text hover:text-white"
            >
              <Pencil size={14} /> Edit dates
            </button>
          </Tooltip>
        )}
        {tab === 'pending' && (
          <>
            <Tooltip content={row.own
                ? 'You cannot approve your own time-off request — it needs a second person'
                : 'Approve'}>
              <button
                onClick={() => approveMutation.mutate(req.id)}
                disabled={row.own || approveMutation.isPending}
                aria-label={row.own
                  ? 'You cannot approve your own time-off request — it needs a second person'
                  : 'Approve'}
                className="flex h-9 w-9 items-center justify-center rounded-fi-control border border-black/20 bg-fi-signal-green text-fi-signal-ink disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Check size={18} />
              </button>
            </Tooltip>
            <button
              onClick={() => setRejectId(req.id)}
              aria-label="Reject request"
              className="flex h-9 w-9 items-center justify-center rounded-fi-control border border-black/20 bg-fi-signal-red text-fi-signal-ink"
            >
              <X size={18} />
            </button>
          </>
        )}
      </div>
    );
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: 'pending', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
    { key: 'calendar', label: 'Calendar' },
  ];
  const tabLabel = tabs.find(t => t.key === tab)?.label ?? '';

  /**
   * The band's figures and the active chip's count: tallies of the one list
   * this screen already loads, with no added query.
   *
   * WHICH LIST, AND WHY THE OTHER CHIPS SHOW A DASH. GET /dispatch/time-off
   * filters by status on the server and returns every matching row — no LIMIT,
   * no pagination — so a tally of the loaded rows is the whole count for the tab
   * in view. It is NOT a count for any other tab: those rows were never fetched,
   * and no endpoint returns per-status counts (the dashboard banner's endpoint
   * counts pending only, and reading it here would be a second query). So an
   * unselected status chip is absent, not zero, and the band describes the tab
   * in view. `null` on the Calendar tab, while in flight, and when the list was
   * refused; displayValue() renders all three as a dash.
   */
  const tally = (() => {
    if (tab === 'calendar' || !Array.isArray(data)) return null;
    const rows = data as any[];
    return {
      requests: rows.length,
      people: new Set(rows.map((r: any) => r.user_id)).size,
      days: requestedDays(rows),
      // Without a user id there is no telling whose a row is, so the figure is
      // absent rather than a confident zero.
      mine: user?.id ? rows.filter(isOwnRequest).length : undefined,
    };
  })();

  return (
    <div data-testid="time-off-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Crew availability"
        title="Time Off"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <button
            type="button"
            onClick={() => { submitMutation.reset(); setShowForm(v => !v); }}
            className="fi-action fi-action--standard fi-action--primary"
          >
            {showForm ? <X className="size-4" aria-hidden="true" /> : <Plus className="size-4" aria-hidden="true" />}
            {showForm ? 'Cancel' : 'Request Time Off'}
          </button>
        }
        stats={[
          {
            icon: <CalendarDays className="size-4" aria-hidden="true" />,
            label: 'Requests',
            sub: 'In the tab shown',
            tone: 'accent',
            loading: isLoading,
            value: displayValue(tally?.requests),
          },
          {
            icon: <Users className="size-4" aria-hidden="true" />,
            label: 'People',
            sub: 'Distinct employees',
            tone: 'info',
            loading: isLoading,
            value: displayValue(tally?.people),
          },
          {
            icon: <Sun className="size-4" aria-hidden="true" />,
            label: 'Days',
            sub: 'Calendar days',
            tone: 'success',
            loading: isLoading,
            value: displayValue(tally?.days),
          },
          {
            icon: <UserRound className="size-4" aria-hidden="true" />,
            label: 'Filed by you',
            sub: 'Your own requests',
            tone: 'neutral',
            loading: isLoading,
            value: displayValue(tally?.mine),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* Status chips. Calendar is a view rather than a status, so it
              carries no count. See `tally` for why an unselected status chip
              reads a dash. */}
          <div className="scrollbar-hide flex gap-2 overflow-x-auto">
            {tabs.map(t => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                    active
                      ? 'bg-fi-amber text-fi-amber-ink'
                      : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {t.label}
                  {/* No count is claimed while the list is in flight. Full ink
                      on the active chip, as on Clients. */}
                  {t.key !== 'calendar' && !(active && isLoading) && (
                    <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>
                      {displayValue(active ? tally?.requests : undefined)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Request form — rendered above the tab content so it is reachable
              from every tab, including Calendar. */}
          {showForm && (
            <div className="bg-fieldiq-card rounded-fi-panel p-4 space-y-3 border border-fieldiq-border">
              <h3 className="font-semibold text-white text-sm">New Request</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-fieldiq-text mb-1 block">Start Date</label>
                  <DatePicker value={startDate} onChange={setStartDate} placeholder="Start" />
                </div>
                <div>
                  <label className="text-xs text-fieldiq-text mb-1 block">End Date</label>
                  <DatePicker value={endDate} onChange={setEndDate} placeholder="End" />
                </div>
              </div>

              <div>
                <label className="text-xs text-fieldiq-text mb-1 block">Type</label>
                <select
                  value={type}
                  onChange={e => setType(e.target.value)}
                  className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white"
                >
                  {TIME_OFF_TYPE_VALUES.map(t => (
                    <option key={t} value={t}>{typeLabels[t]}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs text-fieldiq-text mb-1 block">Reason (optional)</label>
                <textarea
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  rows={2}
                  className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white resize-none"
                  placeholder="Add a note..."
                />
              </div>

              {submitMutation.isError && (
                <p className="text-fi-red text-xs">
                  {(submitMutation.error as any)?.response?.data?.error || 'Failed to submit request'}
                </p>
              )}

              <p className="text-xs text-fieldiq-muted">
                Your request goes to the pending list below for someone else to approve.
                You cannot approve your own.
              </p>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleSubmit}
                  disabled={!startDate || !endDate || submitMutation.isPending}
                  className="fi-action fi-action--standard fi-action--primary disabled:opacity-50"
                >
                  {submitMutation.isPending && <Loader2 size={16} className="animate-spin" />}
                  Submit Request
                </button>
                <button
                  onClick={() => { submitMutation.reset(); setShowForm(false); }}
                  className="rounded-fi-control px-4 py-2 text-sm text-fieldiq-text hover:text-white"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {tab === 'calendar' ? (
            <CalendarView />
          ) : (
            /* The register. The amber rail is on this container; each row
               carries its status rail, never a second amber one. */
            <section
              aria-labelledby="time-off-register-title"
              className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
            >
              <header className="flex items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
                <div>
                  <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Leave register</p>
                  <h2 id="time-off-register-title" className="mt-1 text-base font-extrabold tracking-tight text-white">{tabLabel} requests</h2>
                </div>
              </header>

              {isLoading ? (
                <div className="px-4 py-12 text-center text-fieldiq-text">Loading...</div>
              ) : requestsFailure ? (
                <LoadFailureNotice
                  error={queryFailureError(requestsQuery)}
                  kind={requestsFailure}
                  subject={`${tab} time-off requests`}
                  allowed="Admins, office staff and dispatchers"
                  roleLabel={user?.role ? getRoleLabel(user.role) : undefined}
                />
              ) : requests.length === 0 ? (
                <div className="px-4 py-12 text-center">
                  <CalendarOff size={40} className="mx-auto mb-3 text-fieldiq-muted" />
                  <p className="text-fieldiq-text">No {tab} requests</p>
                </div>
              ) : (
                <>
                  {/* ── Desktop table ──
                      THE ROWS ARE NOT A TARGET, and that is the page's existing
                      behaviour, not an omission: a request has no detail view,
                      so there is nothing for a row to open. No tabIndex, no
                      click, no role — so the fe#647 hazard (a row handler eating
                      Enter meant for Approve or Reject) cannot arise, and the
                      inline buttons are the row's only tab stops. */}
                  <div className="hidden overflow-x-auto md:block">
                    <table className="w-full min-w-[960px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                        {/* Transparent 4px so the columns line up with the railed rows. */}
                        <tr className="border-l-4 border-l-transparent">
                          {['Employee', 'Dates', 'Type', 'Status', 'Notes', 'Actions'].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-fieldiq-muted">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {requests.map((req: any) => {
                          const row = describeRow(req);
                          return (
                            <tr
                              key={req.id}
                              className={`border-t border-l-4 border-fieldiq-border align-top ${
                                STATUS_RAIL[req.status] ?? 'border-l-transparent'
                              }`}
                            >
                              <td className="px-4 py-3">
                                <span className="text-sm font-extrabold text-white">{row.name}</span>
                                {row.own && (
                                  <span className="ml-2 inline-block whitespace-nowrap align-middle rounded-fi-pill bg-fieldiq-dark px-2 py-0.5 text-xs font-medium text-fieldiq-muted">
                                    Your request
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-fieldiq-text">
                                {row.dates}
                              </td>
                              <td className="px-4 py-3">
                                <span className={PILL}>{row.typeLabel}</span>
                              </td>
                              <td className="px-4 py-3">
                                <RequestStatusBadge status={req.status} />
                              </td>
                              <td className="px-4 py-3 max-w-[28rem]">
                                {renderNotes(req, row)}
                              </td>
                              <td className="px-4 py-3">
                                {renderActions(req, row)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* ── Mobile cards ── no rail here; that edge belongs to the
                      surrounding register, never to a card. */}
                  <div className="md:hidden space-y-3 p-4">
                    {requests.map((req: any) => {
                      const row = describeRow(req);
                      return (
                        <div key={req.id} className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-extrabold text-white">
                                {row.name}
                                {row.own && (
                                  <span className="ml-2 inline-block whitespace-nowrap align-middle rounded-fi-pill bg-fieldiq-dark px-2 py-0.5 text-xs font-medium text-fieldiq-muted">
                                    Your request
                                  </span>
                                )}
                              </p>
                              <div className="mt-1 flex items-center gap-2">
                                <CalendarDays size={14} className="text-fi-amber" />
                                <span className="text-sm text-fieldiq-text">{row.dates}</span>
                              </div>
                            </div>
                            <RequestStatusBadge status={req.status} />
                          </div>
                          <div className="mt-2">
                            <span className={PILL}>{row.typeLabel}</span>
                          </div>
                          <div className="mt-2">
                            {renderNotes(req, row)}
                          </div>
                          <div className="mt-3">
                            {renderActions(req, row)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </section>
          )}
        </div>
      </section>

      {/* Edit Dates Modal */}
      {editing && (
        <TimeOffDatesModal
          startDate={editing.start_date}
          endDate={editing.end_date}
          isPending={editMutation.isPending}
          error={
            editMutation.isError
              ? (editMutation.error as any)?.response?.data?.error || 'Failed to update dates'
              : null
          }
          onCancel={() => { editMutation.reset(); setEditing(null); }}
          onSave={({ startDate, endDate }) => editMutation.mutate({ id: editing.id, startDate, endDate })}
        />
      )}

      {/* Reject Modal */}
      {rejectId && (
        <RejectModal
          isPending={rejectMutation.isPending}
          onCancel={() => setRejectId(null)}
          onConfirm={(reason) => rejectMutation.mutate({ id: rejectId, reason })}
        />
      )}
    </div>
  );
}
